import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  InputSearch,
  TableData,
  Pagination,
  ActionDropdown,
  showErrorToast,
} from '@/components';
import { Column } from '@/types/common';
import { useAuth } from '@/contexts/AuthContext';
import {
  getInvoices,
  getInvoiceSummary,
  invoiceStatusGroupParam,
  Invoice,
  InvoiceSummary,
} from '@/services/api/invoices';
import {
  InvoiceStatusBadge,
  InvoiceAgingPill,
  InvoicePositionBadge,
} from './InvoiceStatusBadge';
import InvoiceFilter, {
  InvoiceFilterValue,
  EMPTY_FILTER,
} from './InvoiceFilter';
import InvoiceDetailDrawer from './InvoiceDetailDrawer';
import InvoiceExportModal from './InvoiceExportModal';
import InvoiceActionModal, { InvoiceAction } from './InvoiceActionModal';

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY') : '—';

const partyName = (v?: { name?: string } | string | null): string => {
  if (!v) return '—';
  return typeof v === 'string' ? v : v.name || '—';
};

// ─── Status tabs ──────────────────────────────────────────────────────
// IN_PIPELINE fans out across four individual statuses that haven't reached
// a final confirmation step yet.
type TabKey =
  | 'ALL'
  | 'IN_PIPELINE'
  | 'WAITING_CONFIRMATION'
  | 'DISPUTED'
  | 'RESOLVED';

interface TabDef {
  key: TabKey;
  label: string;
  statuses: string | null; // null → no status filter (all)
  // Tab count derived from the /summary payload.
  count: (s: InvoiceSummary) => number;
}

// In-pipeline statuses each map to a summary field.
const pipelineCount = (s: InvoiceSummary) =>
  (s.pending ?? 0) +
  (s.received ?? 0) +
  (s.receivedByRider ?? 0) +
  (s.exception ?? 0);

const TABS: TabDef[] = [
  {
    key: 'ALL',
    label: 'All',
    statuses: null,
    count: (s) =>
      pipelineCount(s) +
      (s.awaitingConfirmation ?? 0) +
      (s.disputed ?? 0) +
      (s.resolved ?? 0),
  },
  {
    key: 'IN_PIPELINE',
    label: 'In Pipeline',
    statuses: invoiceStatusGroupParam('IN_PIPELINE'),
    count: pipelineCount,
  },
  {
    key: 'WAITING_CONFIRMATION',
    label: 'Waiting Confirmation',
    statuses: invoiceStatusGroupParam('WAITING_CONFIRMATION'),
    count: (s) => s.awaitingConfirmation ?? 0,
  },
  {
    key: 'DISPUTED',
    label: 'Disputed',
    statuses: invoiceStatusGroupParam('DISPUTED'),
    count: (s) => s.disputed ?? 0,
  },
  {
    key: 'RESOLVED',
    label: 'Resolved',
    statuses: invoiceStatusGroupParam('RESOLVED'),
    count: (s) => s.resolved ?? 0,
  },
];

// ─── Summary cards ────────────────────────────────────────────────────
// Three status cards drill into their tab; the red-alerts card toggles the
// `agingTier=red` filter instead. `count` reads the /summary payload.
type CardKind =
  | { type: 'status'; tab: TabKey }
  | { type: 'aging'; tier: string };

interface SummaryCardDef {
  id: string;
  label: string;
  dot: string; // status dot colour (DS semantic token)
  ring: string; // active ring colour
  count: (s: InvoiceSummary) => number | undefined;
  kind: CardKind;
}

const SUMMARY_CARDS: SummaryCardDef[] = [
  {
    id: 'waiting',
    label: 'Waiting Confirmation',
    dot: 'bg-information-base',
    ring: 'ring-information-base',
    count: (s) => s.awaitingConfirmation,
    kind: { type: 'status', tab: 'WAITING_CONFIRMATION' },
  },
  {
    id: 'disputed',
    label: 'Disputed',
    dot: 'bg-error-base',
    ring: 'ring-error-base',
    count: (s) => s.disputed,
    kind: { type: 'status', tab: 'DISPUTED' },
  },
  {
    id: 'resolved',
    label: 'Resolved',
    dot: 'bg-success-base',
    ring: 'ring-success-base',
    count: (s) => s.resolved,
    kind: { type: 'status', tab: 'RESOLVED' },
  },
  {
    id: 'redAlerts',
    label: 'Red Alerts',
    dot: 'bg-error-base',
    ring: 'ring-error-base',
    count: (s) => s.redAlerts,
    kind: { type: 'aging', tier: 'red' },
  },
];

const InvoicesPage: React.FC = () => {
  const { client, user } = useAuth();
  const clientId = client?.id;

  const [activeTab, setActiveTab] = useState<TabKey>('ALL');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<InvoiceFilterValue>(EMPTY_FILTER);
  const [detail, setDetail] = useState<Invoice | null>(null);
  const [summary, setSummary] = useState<InvoiceSummary>({});
  const [exportOpen, setExportOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [actionTarget, setActionTarget] = useState<{
    invoice: Invoice;
    action: InvoiceAction;
  } | null>(null);

  const tabStatuses = TABS.find((t) => t.key === activeTab)?.statuses ?? null;

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoices({
        page,
        pageSize,
        search,
        clientId,
        status: tabStatuses ?? undefined,
        agingTier: filters.agingTiers.join(',') || undefined,
        issuedFrom: filters.issuedFrom || undefined,
        issuedTo: filters.issuedTo || undefined,
      });
      setInvoices(res.data ?? []);
      setTotal(res.pagination?.total ?? 0);
    } catch (error) {
      showErrorToast(
        'Gagal memuat data',
        error instanceof Error ? error.message : 'Terjadi kesalahan.'
      );
      setInvoices([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, clientId, tabStatuses, filters]);

  const fetchSummary = useCallback(async () => {
    setSummary(await getInvoiceSummary({ clientId }));
  }, [clientId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleRefresh = () => {
    fetchInvoices();
    fetchSummary();
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setPage(1);
  };

  // Clicking a summary card. Status cards toggle their tab; the red-alerts
  // card toggles the `agingTier=red` filter (independent of the active tab).
  const handleCardClick = (card: SummaryCardDef) => {
    if (card.kind.type === 'status') {
      const tab = card.kind.tab;
      setActiveTab((prev) => (prev === tab ? 'ALL' : tab));
    } else {
      const tier = card.kind.tier;
      setFilters((f) => ({
        ...f,
        agingTiers: f.agingTiers.includes(tier)
          ? f.agingTiers.filter((t) => t !== tier)
          : [...f.agingTiers, tier],
      }));
    }
    setPage(1);
  };

  const isCardActive = (card: SummaryCardDef): boolean =>
    card.kind.type === 'status'
      ? activeTab === card.kind.tab
      : filters.agingTiers.includes(card.kind.tier);

  const handleApplyFilters = (next: InvoiceFilterValue) => {
    setFilters(next);
    setPage(1);
  };

  const columns: Column<Invoice>[] = [
    {
      key: 'airwaybill',
      title: 'Delivery ID',
      sticky: 'left',
      render: (_v, record) => (
        <span className="font-mono text-xs font-semibold text-primary">
          {record.airwaybill}
        </span>
      ),
    },
    {
      key: 'invoiceNumber',
      title: 'Invoice Number',
      render: (_v, record) => (
        <span className="font-mono text-xs text-text-sub-600">
          {record.invoiceNumber || '—'}
        </span>
      ),
    },
    {
      key: 'outlet',
      title: 'Outlet',
      render: (_v, record) => (
        <span className="font-mono text-xs text-text-sub-600">
          {partyName(record.outlet) || '—'}
        </span>
      ),
    },
    {
      key: 'client',
      title: 'Client',
      render: (_v, record) => (
        <span className="text-xs font-medium text-text-strong-950">
          {partyName(record.client)}
        </span>
      ),
    },
    {
      key: 'rider',
      title: 'Rider',
      render: (_v, record) => (
        <span className="text-xs text-text-sub-600">
          {partyName(record.rider)}
        </span>
      ),
    },
    {
      key: 'itemsCount',
      title: 'Items',
      align: 'center',
      render: (_v, record) => (
        <span className="text-text-strong-950">{record.itemsCount ?? 0}</span>
      ),
    },
    {
      key: 'issueDate',
      title: 'Issue date',
      render: (_v, record) => (
        <span className="text-text-sub-600">
          {formatDate(record.issueDate)}
        </span>
      ),
    },
    {
      key: 'position',
      title: 'Posisi Invoice',
      render: (_v, record) => <InvoicePositionBadge status={record.status} />,
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, record) => <InvoiceStatusBadge status={record.status} />,
    },
    {
      key: 'aging',
      title: 'Aging',
      render: (_v, record) => (
        <InvoiceAgingPill days={record.daysSince} tier={record.agingTier} />
      ),
    },
    {
      key: 'actions',
      title: '',
      render: (_v, record) =>
        // Awaiting-confirmation rows get the resolve/dispute kebab; everything
        // else keeps the plain view-detail eye.
        record.status === 'WAITING_CONFIRMATION' ? (
          <ActionDropdown
            record={record}
            isOpen={openMenuId === record.id}
            onToggle={() =>
              setOpenMenuId((id) => (id === record.id ? null : record.id))
            }
            ariaLabel="Invoice actions"
            actions={[
              {
                key: 'resolve',
                label: 'Mark as Resolved',
                icon: 'ri-checkbox-circle-line',
                className: 'text-success-base',
                onClick: () => {
                  setOpenMenuId(null);
                  setActionTarget({ invoice: record, action: 'resolve' });
                },
              },
              {
                key: 'dispute',
                label: 'Mark as Dispute',
                icon: 'ri-error-warning-line',
                className: 'text-error-base',
                separateAfter: true,
                onClick: () => {
                  setOpenMenuId(null);
                  setActionTarget({ invoice: record, action: 'dispute' });
                },
              },
              {
                key: 'detail',
                label: 'See detail',
                icon: 'ri-eye-line',
                onClick: () => {
                  setOpenMenuId(null);
                  setDetail(record);
                },
              },
            ]}
          />
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setDetail(record);
            }}
            className="text-text-soft-400 hover:text-primary"
            aria-label="View detail"
          >
            <i className="ri-eye-line text-base" />
          </button>
        ),
    },
  ];

  return (
    <div className="p-4 space-y-5 sm:p-6">
      {/* Page head */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-bold text-primary">04</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-soft-400">
              Invoices
            </span>
          </div>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-text-strong-950 sm:text-3xl">
            Invoices
          </h1>
          <p className="max-w-2xl text-sm text-text-sub-600">
            Hanya invoice yang perlu Anda tindak — confirm, dispute, atau cek
            setelahnya. Tutup loop puluhan invoice per minggu tanpa email.
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap gap-3">
          <Button variant="secondary" onClick={handleRefresh}>
            <i className="ri-refresh-line mr-1.5" />
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setExportOpen(true)}>
            <i className="ri-download-2-line mr-1.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary cards — clickable drill-ins */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {SUMMARY_CARDS.map((card) => {
          const active = isCardActive(card);
          const value = card.count(summary);
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => handleCardClick(card)}
              className={`flex flex-col gap-2 rounded-xl border bg-bg-white-0 p-4 text-left shadow-card-xs transition-all hover:shadow-card-sm ${
                active
                  ? `border-transparent ring-2 ${card.ring}`
                  : 'border-stroke-soft-200 hover:border-stroke-sub-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 shrink-0 rounded-full ${card.dot}`} />
                <span className="truncate text-xs font-medium text-text-sub-600">
                  {card.label}
                </span>
              </div>
              <div className="text-2xl font-bold text-text-strong-950">
                {value ?? 0}
              </div>
            </button>
          );
        })}
      </div>

      {/* Table card */}
      <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
        {/* Status tab bar */}
        <div className="flex items-center gap-1 overflow-x-auto border-b border-stroke-soft-200 px-4 pt-1">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            const count = tab.count(summary);
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-3 text-sm font-medium transition-colors focus:outline-none ${
                  isActive
                    ? 'text-primary'
                    : 'text-text-sub-600 hover:text-text-strong-950'
                }`}
              >
                {tab.label}
                <span
                  className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold ${
                    isActive
                      ? 'bg-primary text-static-white'
                      : 'bg-bg-weak-50 text-text-sub-600'
                  }`}
                >
                  {count}
                </span>
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-primary" />
                )}
              </button>
            );
          })}
        </div>

        {/* Search + filter row */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke-soft-200 p-4">
          <InputSearch
            className="min-w-[200px] max-w-[420px] flex-1"
            placeholder="Search invoice number, delivery ID, atau rider…"
            onSearch={handleSearch}
            onClear={() => handleSearch('')}
          />
          <InvoiceFilter value={filters} onApply={handleApplyFilters} />
        </div>

        <TableData<Invoice>
          data={invoices}
          columns={columns}
          loading={loading}
          className="rounded-none border-0 shadow-none"
          onRowClick={(record) => setDetail(record)}
        />

        <div className="px-4 pb-4">
          <Pagination
            currentPage={page}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      </div>

      <InvoiceDetailDrawer
        invoice={detail}
        onClose={() => setDetail(null)}
        onAction={(action) =>
          detail && setActionTarget({ invoice: detail, action })
        }
      />

      <InvoiceActionModal
        action={actionTarget?.action ?? null}
        invoiceId={actionTarget?.invoice.id ?? ''}
        subtitle={
          actionTarget
            ? `${actionTarget.invoice.invoiceNumber} — ${partyName(
                actionTarget.invoice.outlet
              )}`
            : ''
        }
        updatedBy={user?.email ?? ''}
        onClose={() => setActionTarget(null)}
        onDone={() => {
          setDetail(null);
          handleRefresh();
        }}
      />

      <InvoiceExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        clientId={clientId}
      />
    </div>
  );
};

export default InvoicesPage;
